// src/modules/goldVault/pages/TransferApprovalDetail.jsx
//
// Full detail of a transfer's approval workflow (INVENTORY_TRANSFER_APPROVAL):
// which flow was triggered, every step, who can approve, the current step, the
// next step / next approver, the facts that drove routing, and the decision
// history. Renders the Approve/Reject actions when the viewer is an eligible
// approver for the current step. Reused inside TransferApprovals (drawer) and
// the transfer detail page.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Steps, Timeline, Tag, Typography, Row, Col, Spin, Empty, Avatar, Tooltip,
  Descriptions, Alert, Button, Modal, Input, Space, Divider, message,
} from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, RightCircleOutlined,
  SafetyCertificateOutlined, HistoryOutlined, BranchesOutlined, UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getTransferApproval, decideTransferApproval } from '../api';

const { Title, Text } = Typography;

const STEP_STATUS_COLOR = {
  pending: 'processing', approved: 'success', rejected: 'error',
  escalated: 'warning', skipped: 'default',
};
const STEP_ANTD_STATUS = {
  approved: 'finish', rejected: 'error', pending: 'process',
  escalated: 'warning', skipped: 'wait',
};
const MODE_LABEL = {
  SEQUENTIAL: 'Sequential (first approver advances)',
  PARALLEL: 'Parallel',
  FIRST_RESPONSE: 'First response wins',
  ALL_REQUIRED: 'All approvers required',
  MAJORITY: 'Majority required',
};
const ESCALATION_LABEL = {
  REASSIGN: 'Reassign on deadline', NOTIFY_ONLY: 'Notify only',
  AUTO_APPROVE: 'Auto-approve on deadline', AUTO_REJECT: 'Auto-reject on deadline',
  SKIP: 'Skip if no approver',
};

const initials = (name) => (name ? name.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase() : 'U');

const strategyLabel = (step) => {
  const s = step.assignmentStrategy || (step.resolverConfigs && step.resolverConfigs[0]?.assignmentStrategy);
  const cfg = step.resolverConfig || (step.resolverConfigs && step.resolverConfigs[0]?.resolverConfig) || {};
  if (s === 'BRANCH_MANAGER') return 'Source Branch Manager';
  if (s === 'PERMISSION_ROLE') return `Role: ${cfg.roleKey || '—'}${cfg.contextLevel ? ` (${cfg.contextLevel})` : ''}`;
  if (s === 'SPECIFIC_USER') return 'Specific user';
  return s || 'Unassigned';
};

const ApproverList = ({ ids = [], usersMap = {}, empty = 'No approver resolved' }) => {
  if (!ids.length) return <Text type="secondary" italic>{empty}</Text>;
  return (
    <Avatar.Group maxCount={6} size="small" maxStyle={{ color: '#f56a00', backgroundColor: '#fde3cf' }}>
      {ids.map((uid) => {
        const u = typeof uid === 'object' ? uid : usersMap[String(uid)];
        const nm = u?.name || u?.email || String(uid);
        const role = u?.role ? ` · ${u.role}` : '';
        return (
          <Tooltip key={String(uid)} title={`${nm}${role}`}>
            <Avatar style={{ backgroundColor: '#1677ff' }}>{initials(nm)}</Avatar>
          </Tooltip>
        );
      })}
    </Avatar.Group>
  );
};

export default function TransferApprovalDetail({ transferId, onDecided }) {
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [actionModal, setActionModal] = useState({ open: false, type: null });
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [messageApi, ctx] = message.useMessage();

  const load = useCallback(async () => {
    if (!transferId) return;
    setLoading(true);
    try {
      const res = await getTransferApproval(transferId);
      setPayload(res.data || null);
    } catch (e) {
      messageApi.error('Failed to load approval workflow');
    } finally {
      setLoading(false);
    }
  }, [transferId, messageApi]);

  useEffect(() => { load(); }, [load]);

  const submitDecision = async () => {
    if (actionModal.type === 'rejected' && !comment.trim()) {
      messageApi.error('A reason is required to reject');
      return;
    }
    setSubmitting(true);
    try {
      const res = await decideTransferApproval(transferId, { action: actionModal.type, comment });
      messageApi.success(res.message || 'Decision recorded');
      setActionModal({ open: false, type: null });
      setComment('');
      await load();
      onDecided && onDecided();
    } catch (e) {
      messageApi.error(e?.response?.data?.message || 'Failed to record decision');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div style={{ padding: 48, textAlign: 'center' }}><Spin size="large" /></div>;
  if (!payload) return <Empty description="No approval workflow found for this transfer" />;

  const { instance, usersMap, flow, facts, currentStep, currentApprovers, nextStep, nextApprovers, isTerminal, outcome, canAct } = payload;

  const overall = isTerminal
    ? (outcome === 'approved'
        ? { type: 'success', text: 'Approved — transfer released' }
        : { type: 'error', text: 'Rejected — gold auto-returned to the branch vault' })
    : { type: 'info', text: `In progress — awaiting Step ${instance.currentStep}: ${currentStep?.stepName || ''}` };

  const fmtVal = (v) => (v === null || v === undefined || v === '' ? '—' : v);

  return (
    <div>
      {ctx}

      {/* Flow header */}
      <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ padding: 16 }}>
        <Row justify="space-between" align="middle" gutter={[12, 12]}>
          <Col>
            <Title level={5} style={{ margin: 0 }}>
              <SafetyCertificateOutlined style={{ color: '#1677ff', marginRight: 8 }} />
              {flow.templateName || 'Approval Workflow'}
            </Title>
            <Text type="secondary">
              Flow triggered: <Text code>{flow.entityType}</Text> · v{flow.version}
            </Text>
            {flow.templateDescription && (
              <div><Text type="secondary" style={{ fontSize: 12 }}>{flow.templateDescription}</Text></div>
            )}
          </Col>
          {canAct && (
            <Col>
              <Space>
                <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => setActionModal({ open: true, type: 'approved' })}>Approve</Button>
                <Button danger icon={<CloseCircleOutlined />} onClick={() => setActionModal({ open: true, type: 'rejected' })}>Reject</Button>
              </Space>
            </Col>
          )}
        </Row>
      </Card>

      <Alert type={overall.type} showIcon message={overall.text} style={{ marginBottom: 16 }} />

      {/* Current + Next approver summary */}
      {!isTerminal && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={12}>
            <Card size="small" title={<><ClockCircleOutlined /> Current Step — who can approve now</>}>
              <Text strong>Step {currentStep?.stepNumber}: {currentStep?.stepName}</Text>
              <div style={{ margin: '8px 0' }}>
                <Text type="secondary">Assigned via: </Text>{strategyLabel(currentStep || {})}
              </div>
              <ApproverList ids={currentStep?.approvers} usersMap={usersMap} empty="No approver resolved (check role mapping)" />
              {currentStep?.deadline && (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Deadline: {dayjs(currentStep.deadline).format('DD MMM YYYY, hh:mm A')}</Text>
                </div>
              )}
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card size="small" title={<><RightCircleOutlined /> Next Step — next approver</>}>
              {nextStep ? (
                <>
                  <Text strong>Step {nextStep.stepNumber}: {nextStep.stepName}</Text>
                  <div style={{ margin: '8px 0' }}>
                    <Text type="secondary">Assigned via: </Text>{strategyLabel(nextStep)}
                  </div>
                  <ApproverList ids={nextStep.approvers} usersMap={usersMap} empty="Resolved when this step begins" />
                </>
              ) : (
                <Text type="secondary">This is the final step — approving it releases the transfer.</Text>
              )}
            </Card>
          </Col>
        </Row>
      )}

      <Row gutter={[16, 16]}>
        {/* Steps */}
        <Col xs={24} lg={15}>
          <Card size="small" title={<><BranchesOutlined /> Approval Steps</>}>
            <Steps direction="vertical" size="small" current={instance.currentStep - 1}>
              {instance.steps.map((step) => (
                <Steps.Step
                  key={step.stepNumber}
                  status={STEP_ANTD_STATUS[step.status] || 'wait'}
                  title={
                    <Space>
                      <Text strong>Step {step.stepNumber}: {step.stepName}</Text>
                      <Tag color={STEP_STATUS_COLOR[step.status] || 'default'} style={{ textTransform: 'uppercase', fontSize: 10 }}>{step.status}</Tag>
                    </Space>
                  }
                  description={
                    <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: 12, marginTop: 8, marginBottom: 12 }}>
                      <Row gutter={[12, 8]}>
                        <Col span={12}><Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Assigned via</Text>{strategyLabel(step)}</Col>
                        <Col span={12}><Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Approval mode</Text>{MODE_LABEL[step.approvalMode] || step.approvalMode || 'Sequential'}</Col>
                        <Col span={12}><Text type="secondary" style={{ fontSize: 12, display: 'block' }}>On deadline</Text>{ESCALATION_LABEL[step.escalationStrategy] || step.escalationStrategy || '—'}</Col>
                        <Col span={12}><Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Approvers</Text><ApproverList ids={step.approvers} usersMap={usersMap} /></Col>
                        {(step.startedAt || step.completedAt) && (
                          <Col span={24}>
                            <Divider style={{ margin: '6px 0' }} />
                            <Space split={<Divider type="vertical" />} size="small" wrap>
                              {step.startedAt && <Text style={{ fontSize: 12 }}>Started {dayjs(step.startedAt).format('DD MMM, hh:mm A')}</Text>}
                              {step.completedAt && <Text style={{ fontSize: 12 }}>Completed {dayjs(step.completedAt).format('DD MMM, hh:mm A')}</Text>}
                            </Space>
                          </Col>
                        )}
                        {step.comments && step.comments.length > 0 && (
                          <Col span={24}>
                            <Divider style={{ margin: '6px 0' }} />
                            {step.comments.map((c, i) => (
                              <div key={i} style={{ fontSize: 13, marginBottom: 4 }}>
                                <Text strong>{usersMap[String(c.user)]?.name || 'User'}: </Text>
                                <Text>{c.comment}</Text>
                                {c.date && <Text type="secondary" style={{ fontSize: 11 }}> · {dayjs(c.date).format('DD MMM, hh:mm A')}</Text>}
                              </div>
                            ))}
                          </Col>
                        )}
                      </Row>
                    </div>
                  }
                />
              ))}
            </Steps>
          </Card>
        </Col>

        {/* Facts + History */}
        <Col xs={24} lg={9}>
          <Card size="small" title={<><UserOutlined /> Facts used for routing</>} style={{ marginBottom: 16 }}>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Source Branch">{fmtVal(facts.sourceBranchName)}</Descriptions.Item>
              <Descriptions.Item label="Source Branch Type">{fmtVal(facts.sourceBranchType)}</Descriptions.Item>
              <Descriptions.Item label="Destination">{fmtVal(facts.destinationBranchName)}</Descriptions.Item>
              <Descriptions.Item label="Has Branch Manager">{facts.hasSourceBranchManager ? 'Yes' : 'No'}</Descriptions.Item>
              <Descriptions.Item label="Net Weight">{fmtVal(facts.netWeight)} g</Descriptions.Item>
              <Descriptions.Item label="Gross Weight">{fmtVal(facts.grossWeight)} g</Descriptions.Item>
              <Descriptions.Item label="Cases">{fmtVal(facts.caseCount)}</Descriptions.Item>
              <Descriptions.Item label="Est. Value">₹{Number(facts.estimatedValue || 0).toLocaleString('en-IN')}</Descriptions.Item>
            </Descriptions>
          </Card>

          <Card size="small" title={<><HistoryOutlined /> Decision History</>}>
            {instance.history && instance.history.length > 0 ? (
              <Timeline>
                {instance.history.map((h, i) => {
                  const color = h.action === 'approved' ? 'green' : h.action === 'rejected' ? 'red' : h.action === 'escalated' ? 'orange' : 'blue';
                  return (
                    <Timeline.Item key={i} color={color}>
                      <Text strong style={{ textTransform: 'capitalize' }}>{h.action}</Text>
                      <Text type="secondary"> by </Text>
                      <Text strong>{usersMap[String(h.user)]?.name || 'System'}</Text>
                      {h.stepNumber ? <Text type="secondary"> · Step {h.stepNumber}</Text> : null}
                      <div style={{ fontSize: 11, color: '#8c8c8c' }}>{h.timestamp ? dayjs(h.timestamp).format('DD MMM YYYY, hh:mm A') : '-'}</div>
                      {h.comment && <div style={{ background: '#f5f5f5', padding: '4px 8px', borderRadius: 4, fontSize: 12, marginTop: 4 }}>{h.comment}</div>}
                    </Timeline.Item>
                  );
                })}
              </Timeline>
            ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No decisions yet" />}
          </Card>
        </Col>
      </Row>

      <Modal
        open={actionModal.open}
        title={`${actionModal.type === 'approved' ? 'Approve' : 'Reject'} Transfer`}
        onOk={submitDecision}
        confirmLoading={submitting}
        onCancel={() => { setActionModal({ open: false, type: null }); setComment(''); }}
        okText={actionModal.type === 'approved' ? 'Confirm Approve' : 'Confirm Reject'}
        okButtonProps={{ danger: actionModal.type === 'rejected' }}
      >
        {actionModal.type === 'rejected' && (
          <Alert type="warning" showIcon style={{ marginBottom: 12 }}
            message="Rejecting returns the gold to the branch vault and unlinks the cases automatically." />
        )}
        <Text>Comment {actionModal.type === 'rejected' && <Text type="danger">*</Text>}:</Text>
        <Input.TextArea rows={4} value={comment} onChange={(e) => setComment(e.target.value)}
          placeholder={actionModal.type === 'approved' ? 'Optional remarks…' : 'Reason for rejection (required)…'} />
      </Modal>
    </div>
  );
}
